import depsloader from "../../../depsloader";

function getMapsConfig() {
  const c =
    typeof window !== "undefined" && window.SwMapsConfig
      ? window.SwMapsConfig
      : {};
  return {
    searchUrl:
      typeof c.geocoderSearchUrl === "string" && c.geocoderSearchUrl
        ? c.geocoderSearchUrl
        : "https://api.screenwork.de/geocoder/search",
    reverseUrl:
      typeof c.geocoderReverseUrl === "string" && c.geocoderReverseUrl
        ? c.geocoderReverseUrl
        : "https://api.screenwork.de/geocoder/reverse",
    key: typeof c.geocoderKey === "string" ? c.geocoderKey : ""
  };
}

function isFiniteNumber(n) {
  return typeof n === "number" && isFinite(n);
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(l) {
    if (!l || !l.properties) {
      return null;
    }

    const p = Object.assign({}, l.properties);
    const coords =
      l.geometry && Array.isArray(l.geometry.coordinates)
        ? l.geometry.coordinates
        : [];
    const type = typeof p.type === "string" ? p.type : "";
    const geoaddress = {};

    // Straße + Hausnummer (alle Typen, die eine Straße tragen können)
    if (p.street) {
      geoaddress["street"] = p.street;
      if (p.housenumber) {
        geoaddress["number"] = p.housenumber;
      }
    } else if (type === "street" && p.name) {
      geoaddress["street"] = p.name;
    }

    if (p.postcode) {
      geoaddress["zip"] = p.postcode;
    }

    if (type) {
      // Neuer Antworttyp: properties.type und district vorrangig auswerten
      if (p.city) {
        geoaddress["city"] = p.city;
      } else if (
        type === "city" ||
        type === "town" ||
        type === "village" ||
        type === "locality" ||
        type === "hamlet"
      ) {
        if (p.name) {
          geoaddress["city"] = p.name;
        }
      } else if (type === "house" || type === "street") {
        if (p.name && !geoaddress["street"]) {
          geoaddress["city"] = p.name;
        }
      } else if (type !== "district" && p.name) {
        geoaddress["city"] = p.name;
      }

      if (p.district && p.district !== geoaddress["city"]) {
        geoaddress["district"] = p.district;
      } else if (
        type === "district" &&
        p.name &&
        p.name !== geoaddress["city"]
      ) {
        geoaddress["district"] = p.name;
      }
    } else {
      // Legacy-Fallback (alte Photon-/Apps-Antwort ohne type: osm_key/osm_value/name)
      if (p.osm_key === "highway" && p.name && !geoaddress["street"]) {
        geoaddress["street"] = p.name;
        p.name = null;
      }

      if (p.postcode && p.city && p.name) {
        geoaddress["zip"] = p.postcode;
        if (p.city === p.name) {
          geoaddress["city"] = p.city;
        } else {
          geoaddress["city"] = p.city;
          let district = false;
          if (p.osm_key === "boundary") {
            district = true;
          } else if (p.osm_key === "place" && p.osm_value === "suburb") {
            district = true;
          }
          if (district) {
            geoaddress["district"] = p.name;
          }
        }
      } else if (p.postcode && p.city) {
        geoaddress["zip"] = p.postcode;
        geoaddress["city"] = p.city;
      } else if (p.postcode && p.name) {
        geoaddress["zip"] = p.postcode;
        geoaddress["city"] = p.name;
      } else if (p.name) {
        geoaddress["city"] = p.name;
      }
    }

    if (coords.length >= 2) {
      geoaddress["lng"] = coords[0];
      geoaddress["lat"] = coords[1];
    }

    geoaddress["state"] = p.state;
    geoaddress["country"] = p.country;

    geoaddress["text"] = this._geoaddressText(geoaddress);

    return geoaddress;
  }

  geocode(params) {
    return new Promise((resolve, reject) => {
      const cfg = getMapsConfig();
      if (!cfg.key) {
        // Ohne Laufzeit-Key kontrolliert ohne Anfrage enden
        resolve([]);
        return;
      }

      params.set("key", cfg.key);

      const options = { params };
      if (this.axiosRequest != null) {
        options.cancelToken = this.axiosRequest.source.token;
      }

      this.axios
        .get(cfg.searchUrl, options)
        .then(res => {
          const results = [];
          if (res.data && res.data.features) {
            res.data.features.forEach(l => {
              const geoaddress = this._geoaddress(l);
              if (geoaddress) {
                results.push(geoaddress);
              }
            });
          }
          resolve(results);
        })
        .catch(thrown => {
          if (this.axios.isCancel(thrown)) {
            // Abgebrochene Anfrage liefert keine Treffer und keinen Fehler
            resolve([]);
          } else {
            reject(thrown);
          }
        })
        .finally(() => {
          this.axiosRequest = null;
        });
    });
  }

  autocomplete(q) {
    const CancelToken = this.axios.CancelToken;
    this.cancelAutocomplete();
    this.axiosRequest = {
      source: CancelToken.source()
    };

    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("layer", "city");
    params.append("layer", "district");

    return this.geocode(params);
  }

  reverse(lat, lng) {
    return new Promise((resolve, reject) => {
      const cfg = getMapsConfig();
      if (!cfg.key) {
        resolve(null);
        return;
      }

      const latNum = Number(lat);
      const lonNum = Number(lng);
      if (
        !isFiniteNumber(latNum) ||
        !isFiniteNumber(lonNum) ||
        latNum < -90 ||
        latNum > 90 ||
        lonNum < -180 ||
        lonNum > 180
      ) {
        resolve(null);
        return;
      }

      const params = new URLSearchParams();
      params.append("lat", String(latNum));
      params.append("lon", String(lonNum));
      params.append("lang", "de");
      params.append("key", cfg.key);

      this.axios
        .get(cfg.reverseUrl, { params })
        .then(res => {
          if (
            res.data &&
            res.data.features &&
            res.data.features.length > 0
          ) {
            resolve(this._geoaddress(res.data.features[0]));
          } else {
            resolve(null);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    });
  });
}

export { GeocoderFactory };

export default {};
